/**
 * Per-workout custom carb-target overrides (build-spec T3b) surfaced on the
 * fuel-plan Sessions view. The provider exposes get/commit for
 * `customTargets.perWorkout[workoutId]`, so each session card can render an
 * inline override without threading props or re-fetching.
 *
 * Gated to the plan's own race plan (multi-event B2): the control appears
 * whenever the plan on screen belongs to one of the caller's race plans that is
 * still ACTIVE, so an override always writes the right row and archived plans
 * stay read-only. The journey view arrives as a prop — the plan-detail screen
 * fetches it once by id and shares it with the plan hero and body, instead of each
 * surface mounting its own `useJourney` and tripling the /view fetch.
 */
import { createContext, useContext, useState, type ReactNode } from "react";
import { useAuth } from "../../context/AuthProvider";
import {
  getJourneyView,
  patchJourney,
  type CustomTargets,
  type JourneyView
} from "../../lib/journeyApi";
import { mergePerWorkoutTarget, normalizeCarbTargetInput } from "./useCustomCarbTarget";
import type { WorkoutCarbTarget } from "../../lib/fuelPlanTypes";
import "./journeyOverrides.css";

/**
 * Render a raw→resolved carb target as "80 → 37 g/hr" when the two differ, else
 * the plain "37 g/hr". Extracted (Lane L7) so the Edit-workout modal fuel rail
 * (L4) reuses the exact same formatting the Sessions view shows. Inputs are
 * rounded g/hr integers.
 */
export function formatRawResolved(raw: number, resolved: number): string {
  const r = Math.round(raw);
  const res = Math.round(resolved);
  return r !== res ? `${r} → ${res} g/hr` : `${res} g/hr`;
}

/**
 * Render the raw→resolved carb target as "80 → 37 g/hr" when the ramp (or a
 * cascade source) scaled the raw number down, else the plain "37 g/hr". Used by
 * the custom override chip and the per-workout provenance line so a set target
 * never lies about what the plan will actually build (A4).
 */
export function formatCarbResolution(target: WorkoutCarbTarget): string {
  return formatRawResolved(target.raw_g_per_hr, target.resolved_g_per_hr);
}

/** "gut-training week N of M" ramp suffix — server-authored `rampWeekLabel`
 *  wins; otherwise a short generic marker so a ramped session still reads as
 *  scaled. Null when the ramp didn't scale this session (factor ≥ 1). */
export function carbTargetRampSuffix(
  target: WorkoutCarbTarget | null,
  rampWeekLabel: string | null | undefined
): string | null {
  if (!target || target.ramp_factor >= 1) return null;
  return rampWeekLabel && rampWeekLabel.trim() ? rampWeekLabel : "gut-training week";
}

type OverridesCtx = {
  active: boolean;
  get: (workoutId: string) => number | null;
  commit: (workoutId: string, value: number | null) => void;
  saving: boolean;
  /** Workout whose last commit failed (rolled back) — the chip shows a retry hint. */
  errorFor: string | null;
};

const Ctx = createContext<OverridesCtx | null>(null);

export function JourneyOverridesProvider({
  journey,
  refresh,
  children
}: {
  /** The plan's own race-plan view (any status), fetched once by the host. */
  journey: JourneyView | null;
  /** Re-fetches the shared journey view after a successful override write. */
  refresh: () => void;
  children: ReactNode;
}) {
  const { state, refreshCsrf } = useAuth();
  const csrfToken = (state as { csrfToken?: string }).csrfToken ?? "";
  const [local, setLocal] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [errorFor, setErrorFor] = useState<string | null>(null);

  // Overrides only write to a LIVE race plan — archived plans are read-only.
  const active = !!journey && journey.status === "active";
  const serverPerWorkout: Record<string, number> = journey?.customTargets?.perWorkout ?? {};

  const get = (workoutId: string): number | null =>
    local[workoutId] ?? serverPerWorkout[workoutId] ?? null;

  const commit = (workoutId: string, value: number | null) => {
    if (!journey) return;
    // Clamp to the server's accepted band and whole grams — an out-of-band value
    // would 400 and the optimistic chip would lie about what's saved.
    // `normalizeCarbTargetInput` clamps and maps the "unset" 0 → null (shared
    // with the L7 hook so both writers behave identically).
    const clamped = normalizeCarbTargetInput(value);
    const prevLocal = local;
    const journeyId = journey.id;
    // Optimistic chip: fold ONLY this session's edit into `local` (never rebuild
    // the whole map from a snapshot — that resurrected a concurrently-deleted
    // sibling override; Lane 5 §5).
    setLocal((l) => {
      const next = { ...l };
      if (clamped != null) next[workoutId] = clamped;
      else delete next[workoutId];
      return next;
    });
    setSaving(true);
    setErrorFor(null);
    // Re-read the journey fresh, then apply a SINGLE-KEY delta via the shared
    // `mergePerWorkoutTarget` (preserves every sibling — other workouts'
    // overrides, raceDayGramsPerHour, sodium). A concurrent delete of another
    // key therefore survives instead of being clobbered by our open-time copy.
    void getJourneyView(journeyId)
      .then((fresh) => {
        const base = fresh?.customTargets ?? journey.customTargets;
        const customTargets: CustomTargets = mergePerWorkoutTarget(base, workoutId, clamped);
        return patchJourney(journeyId, { customTargets }, csrfToken, refreshCsrf);
      })
      .then(() => refresh())
      .catch(() => {
        // Roll back the optimistic value — the chip must reflect server truth.
        setLocal(prevLocal);
        setErrorFor(workoutId);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Ctx.Provider value={{ active, get, commit, saving, errorFor }}>{children}</Ctx.Provider>
  );
}

/**
 * Inline per-session override control. Renders nothing unless the plan belongs
 * to a LIVE race plan. A set override changes the base During target for that
 * one workout (the ramp still composes on top); regenerate the drop to apply.
 *
 * `duringGated={false}` (targets audit C4): the engine gated this session off
 * during-fuel (duration/family thresholds) and an override can't open that
 * gate — hide the affordance unless an override already exists, in which case
 * show it with an honest "won't add fuel" note so it can be cleared.
 */
export function WorkoutCarbOverride({
  workoutId,
  duringGated = true,
  carbTarget = null,
  rampWeekLabel = null
}: {
  workoutId: string;
  duringGated?: boolean;
  /** Resolved carb-target provenance for this session (A4) — drives the chip's
   *  raw→resolved label and the "regenerate to apply" drift hint (B7). */
  carbTarget?: WorkoutCarbTarget | null;
  /** Canonical server ramp label ("Gut-training week N of M") for the suffix. */
  rampWeekLabel?: string | null;
}) {
  const ctx = useContext(Ctx);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  if (!ctx || !ctx.active) return null;
  const current = ctx.get(workoutId);
  if (!duringGated && current == null) return null;

  const rampSuffix = carbTargetRampSuffix(carbTarget, rampWeekLabel);

  // B7: the customer set an override that the plan hasn't been regenerated to
  // reflect. The chip's set value is the RAW target; the plan's resolved
  // carb_target still shows the previous raw (the ramp scales it, so compare
  // raw-to-raw). A mismatch means "edited, not yet applied".
  const planRaw = carbTarget ? Math.round(carbTarget.raw_g_per_hr) : null;
  const overrideDrifted =
    current != null && planRaw != null && Math.round(current) !== planRaw;

  if (!editing) {
    // Chip reflects the RESOLVED (ramped) number so it can't lie: when a custom
    // override is set and the plan has caught up to it, show the plan's
    // raw→resolved; otherwise fall back to the raw set value.
    const chipLabel = current
      ? carbTarget && !overrideDrifted
        ? `Custom ${formatCarbResolution(carbTarget)}`
        : `Custom ${Math.round(current)} g/hr`
      : "+ Custom carbs";
    return (
      <>
        <button
          type="button"
          className="fgf-override__chip"
          onClick={() => {
            setVal(current ? String(current) : "");
            setEditing(true);
          }}>
          {chipLabel}
        </button>
        {/* Ramp context renders whenever the ramp scaled this session — with or
            without a custom override — so a silently-reduced target is always
            explained. Suppressed while drifted: "Regenerate to apply" owns
            that state. */}
        {rampSuffix && !overrideDrifted && (
          <span className="fgf-override__ramp">· {rampSuffix}</span>
        )}
        {overrideDrifted && (
          <span className="fgf-override__regen">Regenerate to apply</span>
        )}
        {!duringGated && current != null && (
          <span className="fgf-override__err">
            Below the during-fuel threshold — this won't add fuel to this session
          </span>
        )}
        {ctx.errorFor === workoutId && (
          <span className="fgf-override__err">Couldn't save — try again</span>
        )}
      </>
    );
  }

  const save = () => {
    const n = val.trim() === "" ? null : Number(val);
    ctx.commit(workoutId, n != null && Number.isFinite(n) ? n : null);
    setEditing(false);
  };

  return (
    <span className="fgf-override__edit">
      <input
        type="number"
        min={0}
        max={140}
        step={5}
        value={val}
        autoFocus
        aria-label="Custom carbs per hour for this session"
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
      />
      <span className="fgf-override__unit">g/hr</span>
      <button type="button" className="fgf-override__save" onClick={save} disabled={ctx.saving}>
        {ctx.saving ? "…" : "Save"}
      </button>
      <button
        type="button"
        className="fgf-override__x"
        aria-label="Cancel"
        onClick={() => setEditing(false)}>
        ×
      </button>
    </span>
  );
}
